import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getTimelineTwo } from 'utils/apiRequests';
import {
	Header,
	LoadingContainer,
	NoData,
	SectionWrapper,
	Title,
} from '../../Charts/ChartList.styles';
import {
	ApLoaderDefault,
	ApLoaderText,
	ApSimpleDropdown,
	colors,
} from '@alixpartners/ui-components';
import { isUndefined } from 'lodash';
import { renderToString } from 'react-dom/server';
import {
	FilterWrapper,
	ToggleSwitch,
	TooltipContainer,
	TooltipItemTitle,
} from './TimelineTwo.styles';
import { getDateFormat } from 'CategoryManagement/utils';
import moment from 'moment';
import { useToastMessageContext } from 'contexts/ToastMessageContext/ToastMessageProvider';
import { NotificationTypes } from 'components/Toast/constants';
import { formatNumber, formatSpendWithDecimalAndSymbol } from 'utils/number';

const FONTSTYLE = {
	fontSize: '11px',
	fontWeight: '400',
	lineHeight: '13px',
	letterSpacing: 0,
	color: colors.grey7,
};

const mergeAdditionalProperties = data => {
	return data.map(item => {
		const mergedItem = { ...item, ...item.additionalProperties };
		delete mergedItem.additionalProperties;
		return mergedItem;
	});
};

const filterKeywise = (data, filter) => {
	return data.filter(item => {
		return Object.keys(filter).every(key => item[key] === filter[key]);
	});
};

//to scale volume to range 0 to 20
const scaleMarker = (value, min, max) => {
	return Math.round(((value - min) / (max - min)) * 20);
};

const TimelineTwo = ({
	modelId,
	filterData,
	skuDetails,
	groupByKeys,
	currencySymbol,
	hideDecimals,
}) => {
	const defaultGroubByKey = groupByKeys.length > 0 && groupByKeys[0];
	const { sku } = skuDetails;
	const { showToast } = useToastMessageContext();
	const chartRef = useRef();
	const [chartTitle, setChartTitle] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState([]);
	const [filter, setFilter] = useState({
		label: 'PO Wt.Avg Unit Price',
		value: 'avG_UNIT_PRICE_USD',
	});
	const [key, setKey] = useState(defaultGroubByKey);
	const [options, setOptions] = useState({
		chart: {
			type: 'line',
			zoomType: 'x',
		},
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		title: {
			text: '',
			style: {
				...FONTSTYLE,
				textOverflow: 'ellipsis',
			},
		},
		xAxis: {
			type: 'datetime',
			gridLineWidth: 0,
			lineColor: colors.black,
			title: {
				text: 'Date',
				style: {
					...FONTSTYLE,
				},
			},
		},
		yAxis: {
			gridLineWidth: 0,
			lineColor: colors.black,
			lineWidth: 1,
			labels: {
				formatter: function() {
					if (isUndefined(this.value)) {
						return '';
					} else {
						return formatSpendWithDecimalAndSymbol(
							this.value,
							currencySymbol,
							hideDecimals,
							0
						).toUpperCase();
					}
				},
			},
			title: {
				text: filter.label,
				style: {
					...FONTSTYLE,
					textOverflow: 'ellipsis',
				},
			},
		},
		series: [],
		tooltip: {
			shared: false,
			useHTML: true,
			backgroundColor: 'rgba(255,255,255, 0.9)',
			borderWidth: 0,
			borderRadius: 2,
			outside: true,
			formatter: function() {
				const { x, y, series, point } = this;
				const dateObj = new Date(x);
				const date = dateObj.toISOString();
				return renderToString(
					<TooltipContainer>
						<TooltipItemTitle>{series.name}</TooltipItemTitle>
						<div id="tooltip_content">
							<div>Date - {moment(date).format('MMMM YYYY')}</div>
							<div>
								{point?.additionalInfo?.seriesFor} -
								{formatSpendWithDecimalAndSymbol(
									y,
									currencySymbol,
									hideDecimals,
									1
								).toUpperCase()}
							</div>
							<div>
								PO Volume - {formatNumber(point?.additionalInfo?.volume)}
							</div>
						</div>
					</TooltipContainer>
				);
			},
		},
		legend: {
			itemStyle: {
				textTransform: 'uppercase',
			},
		},
	});
	const [isKeyAbsent, setIsKeyAbsent] = useState(false);
	const [isScaledVolume, setIsScaledVolume] = useState(false);

	useEffect(
		() => {
			if (sku !== '') {
				if (data.length === 0) {
				} else {
					let newsupplierSeriesData = [];
					if (Object.keys(filter).length !== 0) {
						newsupplierSeriesData = getSeriesData(
							data,
							filter.value,
							key.value
						);
					}
					setOptions(prevState => ({
						...prevState,
						series: newsupplierSeriesData,
						yAxis: {
							...prevState.yAxis,
							title: {
								...prevState.yAxis.title,
								text: filter.label,
							},
						},
					}));
				}
			}
		}, // eslint-disable-next-line
		[filter, isScaledVolume]
	);

	const fetchData = async () => {
		setIsLoading(true);
		const { dateFrom, dateTo } = getDateFormat(filterData.dateRange.stateRange);
		const payload = {
			filtersDto: {
				sku: [sku],
				category1: filterData.category_1.selectedOption,
				category2: filterData.category_2.selectedOption,
				category3: filterData.category_3.selectedOption,
				commodityCode: filterData.commodity_Code.selectedOption,
				commodityFamily: filterData.commodity_Family.selectedOption,
				originCountry: filterData.origin_Country.selectedOption,
			},
			modelId: modelId,
			startDate: dateFrom,
			endDate: dateTo,
			columnNames: key.value,
		};
		const timelineResponse = await getTimelineTwo(payload);
		if (timelineResponse.succeeded) {
			if (Object.keys(timelineResponse.data).length > 0) {
				const supplierSeries = getSeriesData(
					timelineResponse.data,
					filter.value,
					key.value
				);
				setOptions(prevState => ({
					...prevState,
					series: supplierSeries,
				}));
				setData(timelineResponse.data);
				const distinctSKUDescription = [
					...new Set(timelineResponse.data.map(x => x.skU_Description)),
				];
				const filteredSKUDescription = distinctSKUDescription.filter(
					x => x !== null
				);
				setChartTitle(`SKU Description - ${filteredSKUDescription[0]}`);
				setIsLoading(false);
			}
		} else {
			setIsLoading(false);
			showToast('Something went wrong!', NotificationTypes.danger, 2500);
		}
	};

	useEffect(
		() => {
			if (groupByKeys.length === 0) {
				setIsLoading(false);
				setIsKeyAbsent(true);
				showToast(
					'Please create a key in SKU Settings to view the chart.',
					NotificationTypes.warning,
					3500
				);
			} else {
				fetchData();
			}
		}, // eslint-disable-next-line
		[key]
	);

	const getSeriesData = (data, filterValue, groupedBy) => {
		const distinctCombinations = [];
		const combinationsSet = new Set();

		data.forEach(item => {
			const subCombination = groupedBy
				.map(x => item.additionalProperties[x])
				.join('|');
			const combination = `${item.supplier}|${subCombination}`;

			if (!combinationsSet.has(combination)) {
				combinationsSet.add(combination);
				let tempObj = {
					supplier: item.supplier,
					key: combination,
				};
				groupedBy.forEach(x => {
					tempObj[x] = item.additionalProperties[x];
				});
				distinctCombinations.push(tempObj);
			}
		});

		const filterDataBasedOnDynamicConditions = (allData, obj) => {
			delete obj.key;
			const mergedData = mergeAdditionalProperties(allData);
			return filterKeywise(mergedData, obj);
		};
		const volumes = data.map(item => item.volume);
		const minVolume = Math.min(...volumes);
		const maxVolume = Math.max(...volumes);

		return distinctCombinations.map(el => {
			return {
				name: el.key,
				data: filterDataBasedOnDynamicConditions(data, el)
					.map(item => {
						return {
							x: Date.UTC(item.year, item.month - 1),
							y: Number(item[filterValue]),
							marker: {
								radius: isScaledVolume
									? scaleMarker(item.volume, minVolume, maxVolume)
									: 5,
								symbol: 'circle',
							},
							additionalInfo: {
								volume: item.volume,
								seriesFor: filter.label,
							},
						};
					})
					.sort((a, b) => a.x - b.x),
			};
		});
	};

	return (
		<SectionWrapper>
			<Header>
				<Title>{chartTitle}</Title>
			</Header>
			{isLoading ? (
				<LoadingContainer>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoadingContainer>
			) : (
				<>
					{isKeyAbsent ? (
						<p>Please create a key in SKU Settings to view the chart.</p>
					) : (
						<>
							{data.length === 0 ? (
								<NoData>No data available</NoData>
							) : (
								<>
									<FilterWrapper>
										<div id="price_filter">
											<ApSimpleDropdown
												onChange={e => setFilter(e)}
												options={[
													{
														label: 'PO Wt.Avg Unit Price',
														value: 'avG_UNIT_PRICE_USD',
													},
													{ label: 'LPP (Last Price Paid)', value: 'lpp' },
												]}
												selected={filter}
												label="Select Option"
											/>
										</div>
										<div id="keys_filter">
											<ApSimpleDropdown
												onChange={e => setKey(e)}
												options={groupByKeys}
												selected={key}
												label="Select Option"
											/>
										</div>
										<div id="scale_marker_by_volume">
											<div>Volume Scale Marker:</div>
											<ToggleSwitch
												defaultChecked={isScaledVolume}
												onChange={() => setIsScaledVolume(!isScaledVolume)}
											/>
										</div>
										<div>Key: Supplier AND {key.label}</div>
									</FilterWrapper>
									<HighchartsReact
										highcharts={Highcharts}
										options={options}
										ref={chartRef}
									/>
								</>
							)}
						</>
					)}
				</>
			)}
		</SectionWrapper>
	);
};

export default TimelineTwo;
